export interface TrendPoint {
  key: string;
  /** Was unter dem Punkt steht, wenn er beschriftet wird. */
  label: string;
  value: number;
}

interface TrendChartProps {
  data: readonly TrendPoint[];
  caption: string;
  /** Der Wert der obersten Rasterlinie, wie er dasteht. */
  maxLabel?: string;
  /** Farbe der Linie. Vorgabe ist der erste Ton der Palette. */
  colour?: string;
}

/** Das Koordinatensystem des ``viewBox``. Die Anzeige skaliert es. */
const WIDTH = 600;
const HEIGHT = 160;
const PADDING = 4;

/**
 * Ein Verlauf ueber die Zeit -- Linie mit Flaeche darunter.
 *
 * **Fuer die Zeitachse eine Linie und keine Balken.** Eine Reihe von dreissig
 * Balken zeigt dreissig Einzelwerte; eine Linie zeigt die Bewegung, und genau
 * die ist die Frage ("wird es mehr?"). Der Einzelwert steht in der Tabelle.
 *
 * **Die Achse beginnt bei null.** Ein abgeschnittener Nullpunkt macht aus
 * einer Schwankung von drei Prozent einen Absturz. In einer Auswertung, die
 * jemandem vorgelegt wird, ist das keine Gestaltungsfrage.
 *
 * Die Strichstaerke bleibt beim Skalieren gleich -- ``vector-effect``
 * besorgt das. Ohne diesen Hinweis zieht der Browser die Linie mit der
 * Flaeche in die Breite, und aus einem Haarstrich wird ein Band.
 */
export function TrendChart({ data, caption, maxLabel, colour }: TrendChartProps) {
  const stroke = colour ?? 'var(--chart-1)';
  const ceiling = Math.max(...data.map((point) => point.value), 1);

  const x = (index: number) =>
    data.length <= 1 ? WIDTH / 2 : (index / (data.length - 1)) * (WIDTH - 2 * PADDING) + PADDING;
  const y = (value: number) => HEIGHT - PADDING - (value / ceiling) * (HEIGHT - 2 * PADDING);

  const line = data
    .map((point, index) => `${String(x(index))},${String(y(point.value))}`)
    .join(' ');
  const area = `${String(x(0))},${String(HEIGHT - PADDING)} ${line} ${String(
    x(data.length - 1),
  )},${String(HEIGHT - PADDING)}`;

  const first = data.at(0);
  const last = data.at(-1);

  return (
    <div className="chart-trend">
      <svg
        viewBox={`0 0 ${String(WIDTH)} ${String(HEIGHT)}`}
        preserveAspectRatio="none"
        role="img"
        aria-label={caption}
        className="chart-trend__svg"
      >
        {/* Drei Rasterlinien: null, die Haelfte, das Maximum. Mehr Linien
            sind mehr Striche und nicht mehr Orientierung. */}
        {[0, 0.5, 1].map((share) => (
          <line
            key={share}
            x1={PADDING}
            x2={WIDTH - PADDING}
            y1={y(ceiling * share)}
            y2={y(ceiling * share)}
            stroke="var(--chart-grid)"
            strokeWidth="1"
            vectorEffect="non-scaling-stroke"
          />
        ))}
        <polygon points={area} fill={stroke} opacity="0.12" />
        <polyline
          points={line}
          fill="none"
          stroke={stroke}
          strokeWidth="2"
          strokeLinejoin="round"
          strokeLinecap="round"
          vectorEffect="non-scaling-stroke"
        />
      </svg>

      <div className="chart-trend__axis">
        <span>{first?.label ?? ''}</span>
        {maxLabel ? <span className="chart-trend__max">{maxLabel}</span> : null}
        <span>{last?.label ?? ''}</span>
      </div>
    </div>
  );
}
